/**
 * useTrade — React hook for player-to-player trading over the REAL server
 * trade protocol (`src/server/handlers/TradeHandler.ts`).
 *
 * Commands use `room.send(...)`; responses are bound to the current
 * room/session/generation and trade ID. Offer and completion state comes
 * from the server, with one outstanding local command at a time.
 *
 * Server message contract (all verified in TradeHandler.register):
 *   Client → Server (room.send):
 *     tradeRequest    { targetSessionId }   — start a trade with a nearby player
 *     tradeAccept     {}                     — (target) accept an incoming invite
 *     tradeDecline    {}                     — (target) decline an incoming invite
 *     tradeOfferItem  { itemId, quantity }   — add an item to your side
 *     tradeOfferGold  { amount }             — set your gold offer (absolute)
 *     tradeLock       {}                     — lock your side
 *     tradeUnlock     {}                     — unlock your side (resets accepts)
 *     tradeConfirm    {}                     — confirm (executes when both confirm)
 *     tradeCancel     {}                     — cancel the trade
 *   Server → Client:
 *     tradeInviteReceived { tradeId, initiatorName, initiatorSessionId }
 *     tradeRequestSent    { tradeId, targetName }
 *     tradeStarted        { tradeId }
 *     tradeUpdate         { tradeId, initiatorOffer, targetOffer, state }
 *     tradeBothLocked     { tradeId }
 *     tradeCompleted      { tradeId }
 *     tradeCancelled      { tradeId, reason }
 *     tradeError          { reason }
 *
 * A "trade" only becomes active once the server sends `tradeStarted`; until
 * then the hook tracks the pending invite / outgoing request. No fabricated
 * partner — every piece of state comes from the wire.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { TRADE_INPUT_LIMITS } from './tradeInputLimits';
import { ServiceRegistry } from '../../core/ServiceRegistry';

/** One side of a live trade as pushed by the server (`tradeUpdate`). */
export interface TradeSideOffer {
  items: Array<{ itemId: string; quantity: number }>;
  gold: number;
  locked: boolean;
  accepted: boolean;
}

/** Server trade lifecycle, including actual completion/cancellation messages. */
export type TradePhase = 'pending' | 'active' | 'locked' | 'completed' | 'cancelled';

/** An incoming trade invite awaiting accept/decline. */
export interface PendingTradeInvite {
  tradeId: string;
  initiatorName: string;
  initiatorSessionId: string;
}

/** Empty offer used as the default for either side before the wire fills it in. */
const EMPTY_OFFER: TradeSideOffer = { items: [], gold: 0, locked: false, accepted: false };

/** Normalize a raw server offer (possibly partial / undefined) into a TradeSideOffer. */
function normalizeOffer(raw: any): TradeSideOffer {
  if (!raw) return EMPTY_OFFER;
  return {
    items: Array.isArray(raw.items) ? raw.items : [],
    gold: typeof raw.gold === 'number' ? raw.gold : 0,
    locked: !!raw.locked,
    accepted: !!raw.accepted,
  };
}

/**
 * Map a server `tradeUpdate` payload onto this client's own/their perspective.
 *
 * The server always reports `initiatorOffer` + `targetOffer`; whether "mine" is
 * the initiator or the target side depends on which role THIS client played.
 * Pure so it can be unit-tested without a renderer or live room.
 */
export function mapTradeUpdate(
  update: { initiatorOffer?: any; targetOffer?: any },
  isInitiator: boolean,
): { mine: TradeSideOffer; theirs: TradeSideOffer } {
  const initiator = normalizeOffer(update.initiatorOffer);
  const target = normalizeOffer(update.targetOffer);
  return isInitiator
    ? { mine: initiator, theirs: target }
    : { mine: target, theirs: initiator };
}

export interface TradeState {
  /** True once the server has confirmed an active trade (`tradeStarted`). */
  active: boolean;
  /** Server trade id of the live trade, or null. */
  tradeId: string | null;
  /** Current server-side trade phase, or null when no trade exists. */
  phase: TradePhase | null;
  /** The local player's side of the trade. */
  myOffer: TradeSideOffer;
  /** The other player's side of the trade. */
  theirOffer: TradeSideOffer;
  /** Display name of the trade partner, when known. */
  partnerName: string | null;
  /** True if this client INITIATED the trade (vs. accepted an invite). */
  isInitiator: boolean;
  /** An incoming invite awaiting accept/decline (target side). */
  pendingInvite: PendingTradeInvite | null;
  /** True after the server acknowledges `tradeRequest`, before the target accepts. */
  outgoingRequest: boolean;
  /** Last error reason from the server (`tradeError`), or null. */
  error: string | null;
  partnerSessionId: string | null;
  pendingAction: string | null;
  lastOutcome: { kind: 'completed' | 'cancelled' | 'uncertain'; message: string; tradeId: string | null } | null;
}

export interface TradeActions {
  /** Start a trade with a nearby player by their session id. */
  requestTrade: (targetSessionId: string) => void;
  /** Accept an incoming trade invite. */
  acceptInvite: () => void;
  /** Decline an incoming trade invite. */
  declineInvite: () => void;
  /** Add `quantity` of an item to the local offer; defaults to 1. */
  offerItem: (itemId: string, quantity?: number) => void;
  /** Set the local gold offer to an absolute `amount`. */
  offerGold: (amount: number) => void;
  /** Lock the local offer. */
  lock: () => void;
  /** Unlock the local offer (resets both accepts). */
  unlock: () => void;
  /** Confirm the trade (executes once both sides confirm). */
  confirm: () => void;
  /** Cancel the trade. */
  cancel: () => void;
  /** Clear the last server error. */
  clearError: () => void;
}

interface TradeRoom {
  sessionId: string;
  send(type: string, data: unknown): void;
  onMessage(type: string, callback: (data: any) => void): (() => void) | void;
}
interface Binding { room: TradeRoom; sessionId: string; generation: number }
interface OwnedTrade extends Binding {
  id: string | null; initiator: boolean; partner: string;
  active: boolean; phase: TradePhase;
}
interface PendingCommand { kind: string; expects?: (mine: TradeSideOffer) => boolean }

function liveRoom(): TradeRoom | null {
  const direct = (globalThis as any).nm?.getRoom?.();
  if (direct) return direct;
  try { return ServiceRegistry.getInstance().get('NetworkManager', { optional: true })?.getRoom?.() ?? null; }
  catch { return null; }
}

/** Commands and replies belong to one room/session/binding generation and trade. */
export function useTrade(): TradeState & TradeActions {
  const [tradeId, setTradeId] = useState<string | null>(null);
  const [phase, setPhase] = useState<TradePhase | null>(null);
  const [myOffer, setMyOffer] = useState<TradeSideOffer>(EMPTY_OFFER);
  const [theirOffer, setTheirOffer] = useState<TradeSideOffer>(EMPTY_OFFER);
  const [partnerName, setPartnerName] = useState<string | null>(null);
  const [partnerSessionId, setPartnerSessionId] = useState<string | null>(null);
  const [isInitiator, setIsInitiator] = useState(false);
  const [active, setActive] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<PendingTradeInvite | null>(null);
  const [outgoingRequest, setOutgoingRequest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [lastOutcome, setLastOutcome] = useState<TradeState['lastOutcome']>(null);
  const binding = useRef<Binding | null>(null);
  const owned = useRef<OwnedTrade | null>(null);
  const pending = useRef<PendingCommand | null>(null);
  const offers = useRef({ mine: myOffer, theirs: theirOffer });
  offers.current = { mine: myOffer, theirs: theirOffer };

  const settleCommand = useCallback(() => { pending.current = null; setPendingAction(null); }, []);
  const finish = useCallback((kind: 'completed' | 'cancelled' | 'uncertain', message: string) => {
    const id = owned.current?.id ?? null;
    owned.current = null;
    offers.current = { mine: EMPTY_OFFER, theirs: EMPTY_OFFER };
    settleCommand();
    setTradeId(null); setActive(false); setPendingInvite(null); setOutgoingRequest(false);
    setMyOffer(EMPTY_OFFER); setTheirOffer(EMPTY_OFFER);
    setPhase(kind === 'uncertain' ? null : kind);
    setLastOutcome({ kind, message, tradeId: id });
  }, [settleCommand]);

  useEffect(() => {
    let generation = 0;
    let disposers: Array<() => void> = [];
    let connectedKey: string | null = null;
    let blocked: TradeRoom | null = null;
    let mounted = true;
    const release = () => {
      generation++;
      for (const dispose of disposers) dispose();
      disposers = []; binding.current = null;
    };
    const bind = () => {
      const room = liveRoom();
      if (room && room === blocked) return;
      if (binding.current?.room === room && binding.current?.sessionId === room?.sessionId) return;
      if (owned.current) finish('uncertain', 'Connection changed. Check your inventory before starting another trade.');
      release();
      if (!room) return;
      const owner = { room, sessionId: room.sessionId, generation };
      binding.current = owner;
      // Test generation before reading the current loan: a queued callback
      // from a released same-object/same-sid transport must not settle it.
      const current = () => mounted && binding.current === owner && owner.generation === generation
        && liveRoom() === room && room.sessionId === owner.sessionId;
      const matches = (data: any) => current() && owned.current?.id != null && data?.tradeId === owned.current.id;
      const on = (type: string, callback: (data: any) => void) => {
        const dispose = room.onMessage(type, callback);
        if (typeof dispose === 'function') disposers.push(dispose);
      };
      on('tradeInviteReceived', (data) => {
        if (!current() || owned.current?.id || !data?.tradeId || !data.initiatorSessionId
          || data.initiatorSessionId === owner.sessionId) return;
        const cancelling = pending.current?.kind === 'cancel';
        if (!cancelling) settleCommand();
        owned.current = { ...owner, id: data.tradeId, initiator: false, partner: data.initiatorSessionId, active: false, phase: 'pending' };
        setTradeId(data.tradeId); setPhase('pending'); setIsInitiator(false);
        setPartnerSessionId(data.initiatorSessionId); setPartnerName(data.initiatorName ?? 'Player');
        setLastOutcome(null); setError(null);
        setPendingInvite({ tradeId: data.tradeId, initiatorName: data.initiatorName ?? 'Player', initiatorSessionId: data.initiatorSessionId });
      });
      on('tradeRequestSent', (data) => {
        const loan = owned.current;
        if (!current() || !loan?.initiator || loan.id || !['request', 'cancel'].includes(pending.current?.kind ?? '') || !data?.tradeId) return;
        loan.id = data.tradeId;
        setTradeId(data.tradeId); setPhase('pending'); setOutgoingRequest(true);
        setPartnerName(data.targetName ?? 'Player');
        if (pending.current?.kind === 'request') settleCommand();
      });
      on('tradeStarted', (data) => {
        if (!matches(data)) return;
        const loan = owned.current!;
        loan.active = true; loan.phase = 'active';
        setActive(true); setPhase('active'); setOutgoingRequest(false); setPendingInvite(null);
        if (pending.current?.kind === 'accept') settleCommand();
      });
      on('tradeUpdate', (data) => {
        if (!matches(data) || !owned.current!.active || !['active', 'locked'].includes(data.state)) return;
        const next = mapTradeUpdate(data, owned.current!.initiator);
        offers.current = next;
        setMyOffer(next.mine); setTheirOffer(next.theirs);
        owned.current!.phase = data.state; setPhase(data.state);
        if (pending.current?.expects?.(next.mine)) settleCommand();
      });
      on('tradeBothLocked', (data) => {
        if (!matches(data) || !owned.current!.active) return;
        owned.current!.phase = 'locked'; setPhase('locked');
        const next = { mine: { ...offers.current.mine, locked: true }, theirs: { ...offers.current.theirs, locked: true } };
        offers.current = next; setMyOffer(next.mine); setTheirOffer(next.theirs);
        if (pending.current?.kind === 'lock') settleCommand();
      });
      on('tradeCompleted', (data) => {
        if (!matches(data) || !owned.current!.active || owned.current!.phase !== 'locked') return;
        // The wire proves completion, but carries no detailed transfer receipt.
        finish('completed', 'Trade completed. Check your inventory to review the exchange.');
      });
      on('tradeCancelled', (data) => {
        if (!matches(data)) return;
        finish('cancelled', data.reason ? `Trade cancelled: ${data.reason}` : 'Trade cancelled.');
      });
      on('tradeError', (data) => {
        if (!current() || !owned.current || !pending.current) return;
        // Errors have no trade/request ID on the existing wire. Only an
        // outstanding command in this binding can own one; exact same-binding
        // error correlation remains unavailable without a protocol change.
        const kind = pending.current.kind;
        setError(data?.reason ?? 'Trade request refused.'); settleCommand();
        if (kind === 'request' || !owned.current?.id) { owned.current = null; setOutgoingRequest(false); setPhase(null); }
      });
    };
    bind();
    const connected = EventBus.getInstance().on(EVENTS.NET_CONNECTED, (data) => {
      const key = JSON.stringify([data.roomId, data.roomInstanceId, data.sessionId, data.clientInstanceId]);
      blocked = null;
      if ((connectedKey !== null && connectedKey !== key) || (connectedKey === null && owned.current)) {
        if (owned.current) finish('uncertain', 'Connection changed. Check your inventory before starting another trade.');
        release();
      }
      connectedKey = key; bind();
    });
    const disconnected = EventBus.getInstance().on(EVENTS.NET_DISCONNECTED, () => {
      blocked = binding.current?.room ?? null;
      if (owned.current) finish('uncertain', 'Connection lost. Check your inventory after reconnecting.');
      release();
    });
    const timer = setInterval(bind, 500);
    return () => {
      const loan = owned.current;
      if (loan && pending.current?.kind !== 'cancel' && liveRoom() === loan.room && loan.room.sessionId === loan.sessionId) {
        try { loan.room.send('tradeCancel', {}); } catch { /* disconnected owner */ }
      }
      mounted = false; owned.current = null; pending.current = null;
      connected(); disconnected(); clearInterval(timer); release();
    };
  }, [finish, settleCommand]);

  const send = useCallback((kind: string, type: string, data = {}, expects?: PendingCommand['expects']): boolean => {
    const owner = binding.current;
    const loan = owned.current;
    if (!owner || !loan || loan.room !== owner.room || loan.generation !== owner.generation
      || liveRoom() !== owner.room || owner.sessionId !== owner.room.sessionId) {
      setError('Connect to a realm before trading.'); return false;
    }
    if (pending.current && kind !== 'cancel') return false;
    const command = { kind, expects };
    pending.current = command; setPendingAction(kind); setError(null);
    try { owner.room.send(type, data); return true; }
    catch {
      if (pending.current === command) settleCommand();
      setError('Trade request could not be sent. Check your connection.');
      return false;
    }
  }, [settleCommand]);

  const requestTrade = useCallback((targetSessionId: string) => {
    const owner = binding.current;
    if (!owner || liveRoom() !== owner.room || owner.room.sessionId !== owner.sessionId) {
      setError('Connect to a realm before trading.'); return;
    }
    if (!owner || owned.current || !targetSessionId || targetSessionId === owner.sessionId) return;
    owned.current = { ...owner, id: null, initiator: true, partner: targetSessionId, active: false, phase: 'pending' };
    setIsInitiator(true); setPartnerSessionId(targetSessionId); setPartnerName(null); setLastOutcome(null);
    if (!send('request', 'tradeRequest', { targetSessionId })) owned.current = null;
  }, [send]);
  const acceptInvite = useCallback(() => { if (owned.current?.phase === 'pending' && !owned.current.initiator) send('accept', 'tradeAccept'); }, [send]);
  const declineInvite = useCallback(() => { if (owned.current?.phase === 'pending' && !owned.current.initiator) send('decline', 'tradeDecline'); }, [send]);
  const offerItem = useCallback((itemId: string, quantity = 1) => {
    if (!owned.current?.active || offers.current.mine.locked || !Number.isInteger(quantity) || quantity <= 0 || quantity > TRADE_INPUT_LIMITS.quantity) return;
    const expected = (offers.current.mine.items.find(item => item.itemId === itemId)?.quantity ?? 0) + quantity;
    send('item', 'tradeOfferItem', { itemId, quantity }, mine => mine.items.some(item => item.itemId === itemId && item.quantity === expected));
  }, [send]);
  const offerGold = useCallback((amount: number) => {
    if (!owned.current?.active || offers.current.mine.locked || !Number.isInteger(amount) || amount < 0 || amount > TRADE_INPUT_LIMITS.gold) return;
    send('gold', 'tradeOfferGold', { amount }, mine => mine.gold === amount);
  }, [send]);
  const lock = useCallback(() => { if (owned.current?.active && !offers.current.mine.locked) send('lock', 'tradeLock', {}, mine => mine.locked); }, [send]);
  const unlock = useCallback(() => { if (owned.current?.active && offers.current.mine.locked) send('unlock', 'tradeUnlock', {}, mine => !mine.locked && !mine.accepted); }, [send]);
  const confirm = useCallback(() => {
    if (owned.current?.phase === 'locked' && offers.current.mine.locked && offers.current.theirs.locked && !offers.current.mine.accepted)
      send('confirm', 'tradeConfirm', {}, mine => mine.accepted);
  }, [send]);
  const cancel = useCallback(() => {
    if (!owned.current) return;
    send('cancel', 'tradeCancel');
  }, [send, settleCommand]);
  const clearError = useCallback(() => setError(null), []);
  return { active, tradeId, phase, myOffer, theirOffer, partnerName, partnerSessionId, isInitiator,
    pendingInvite, outgoingRequest, error, pendingAction, lastOutcome,
    requestTrade, acceptInvite, declineInvite, offerItem, offerGold, lock, unlock, confirm, cancel, clearError };
}
